import {
  TFormatNumberSuffix,
  formatNumberForTable,
  formatWeight,
} from "@/Utils/formatFunctions";
import { useTrans } from "@/translations";

import React, { useEffect, useMemo, useRef, useState } from "react";

import { BarDatum, ResponsiveBar } from "@nivo/bar";
import { createBandScale } from "@nivo/scales";
import hexContrastColor from "hex-contrast-color";
import { twMerge } from "tailwind-merge";

export const BarChart: React.FC<{
  data: BarDatum[];
  unit: TFormatNumberSuffix;
  legendName: string;
  useLabel?: boolean;
  showAverage?: boolean;
  showLegend?: boolean;
  enableGridY?: boolean;
  axisLeft?: { [k: string]: any } | null;
  margin?: { [k: string]: number };
  maxValue?: number | "auto";
  showTickColors?: boolean;
  colors?: { [k: string]: string };
  valueType?: "percentage" | "weight";
}> = ({
  data,
  unit = "",
  legendName,
  useLabel = true,
  showAverage = true,
  showLegend = true,
  enableGridY = false,
  axisLeft = null,
  margin = { top: 0, right: 0, bottom: 50, left: 2 },
  maxValue = "auto",
  showTickColors = false,
  colors = {
    average: "#DDCAB9",
    value: "#72723B",
  },
  valueType = "percentage",
}) => {
  const { t } = useTrans();

  const containerRef = useRef(null);
  const indexBy = "name";

  const [containerWidth, setContainerWidth] = useState(0);
  useEffect(() => {
    if (!containerRef.current) return;
    // Observe the width of the container so we can adjust the bar width accordingly
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const barWidth = useMemo(() => {
    if (containerRef.current === 0) return 0;

    const innerWidth = containerWidth - margin.left - margin.right;

    // recreate the same band scale Nivo uses
    const indexScale = createBandScale(
      { round: false, type: "band" },
      { all: data.map((d) => d[indexBy]), min: 0, max: innerWidth },
      innerWidth,
      "x",
    );
    return indexScale.bandwidth();
  }, [containerWidth, data, indexBy, margin]);

  const keys = ["value"];
  if (showAverage) {
    keys.push("average");
  }

  return (
    <div className="flex h-full flex-col justify-center">
      <div ref={containerRef} className={twMerge("h-[293px]")}>
        <ResponsiveBar
          data={data}
          keys={keys}
          groupMode="grouped"
          indexBy={indexBy}
          isInteractive={true}
          enableGridY={enableGridY}
          enableGridX={false}
          axisLeft={axisLeft}
          margin={margin}
          colors={({ id }) => colors[id]}
          label={(v) => (useLabel ? v.formattedValue : "")}
          labelTextColor={({ data: { id } }) => hexContrastColor(colors[id])}
          innerPadding={2}
          axisBottom={{
            tickSize: 0,
            tickPadding: 8,
            tickRotation: 0,
            renderTick: ({ tickIndex, value, x, y }) => {
              const width = barWidth;
              const fontSize = 11;
              const lineHeight = fontSize * 1.2;
              const maxLines = 3;
              const maxHeight = lineHeight * maxLines;

              return (
                <g
                  transform={`translate(${x}, ${y})`}
                  style={{ opacity: 1, display: "inline" }}
                >
                  {showTickColors && (
                    <circle
                      r="5"
                      cx={-width / 2 - 8}
                      cy={15}
                      fill={data[tickIndex]?.color?.toString()}
                    />
                  )}

                  <foreignObject
                    x={-width / 2}
                    y={5}
                    width={width}
                    height={maxHeight}
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <div
                      style={{
                        fontSize: `${fontSize}px`,
                        lineHeight: `${lineHeight}px`,
                        textAlign: "center",
                        display: "-webkit-box",
                        WebkitBoxOrient: "vertical",
                        WebkitLineClamp: maxLines,
                        overflow: "hidden",
                      }}
                    >
                      {value}
                    </div>
                  </foreignObject>
                </g>
              );
            },
          }}
          tooltipLabel={({ indexValue, data }) =>
            String(data.fullName ?? indexValue)
          }
          valueFormat={(value) =>
            valueType === "weight"
              ? formatWeight(value, { suffix: unit })
              : formatNumberForTable(value, {
                  suffix: unit,
                  numDecimals: value % 1 ? 1 : 0,
                })
          }
          labelSkipHeight={15}
          theme={{
            text: {
              fontFamily: `"DM Sans", sans-serif`,
            },
          }}
          valueScale={{ type: "linear", nice: true, max: maxValue }}
        />
      </div>
      {showLegend && (
        <div className="flex items-center justify-center">
          <p className="font-dm-sans flex items-center pr-8 text-xs text-black last:pr-0">
            <span
              className="mr-2 inline-flex h-1 w-8"
              style={{
                background: colors.value,
              }}
            />
            {legendName}
          </p>
          {showAverage && (
            <p className="font-dm-sans flex items-center pr-8 text-xs text-black last:pr-0">
              <span
                className="mr-2 inline-flex h-1 w-8"
                style={{
                  background: colors.average,
                }}
              />
              {t("statisticsLabels:averageForCarrotCenters")}
            </p>
          )}
        </div>
      )}
    </div>
  );
};
